import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Flag, AlertTriangle, CheckCircle, XCircle, Eye, Trash2, User, MessageSquare, FileText, Image, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

export default function AdminContentModeration() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchReports();
    fetchStats();
  }, [user, navigate, filter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/admin/reports?status=${filter}`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/reports/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleResolve = async (reportId, action) => {
    const confirmMessage = action === 'user_banned' 
      ? 'This will BAN the user. Are you sure?' 
      : action === 'content_removed'
      ? 'This will REMOVE the content. Are you sure?'
      : 'Dismiss this report?';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await apiClient.put(`/admin/reports/${reportId}/resolve?action=${action}&admin_notes=${encodeURIComponent(adminNotes)}`);
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : 'resolved'}`);
      setSelectedReport(null);
      setAdminNotes('');
      fetchReports();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve report');
    }
  };

  const getReasonBadge = (reason) => {
    const styles = {
      inappropriate_content: 'bg-red-500/20 text-red-400',
      spam: 'bg-orange-500/20 text-orange-400',
      harassment: 'bg-pink-500/20 text-pink-400',
      fake: 'bg-yellow-500/20 text-yellow-400',
      other: 'bg-gray-500/20 text-gray-400'
    };
    return styles[reason] || styles.other;
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'profile': return <User size={16} />;
      case 'review': return <MessageSquare size={16} />;
      case 'message': return <MessageSquare size={16} />;
      case 'blog_post': return <FileText size={16} />;
      default: return <Flag size={16} />;
    }
  };

  const formatReason = (reason) => {
    return reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading && reports.length === 0) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Shield className="text-violet-400" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Content Moderation</h1>
            <p className="text-white/60">Review and moderate reported content</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="glass-default border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-yellow-400">{stats.pending}</div>
                <div className="text-sm text-white/60">Pending</div>
              </CardContent>
            </Card>
            <Card className="glass-default border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400">{stats.resolved}</div>
                <div className="text-sm text-white/60">Resolved</div>
              </CardContent>
            </Card>
            <Card className="glass-default border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-white/40">{stats.dismissed}</div>
                <div className="text-sm text-white/60">Dismissed</div>
              </CardContent>
            </Card>
            <Card className="glass-default border-white/10">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-red-400">{stats.by_type?.profile || 0}</div>
                <div className="text-sm text-white/60">Profile Reports</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'resolved', 'dismissed'].map((status) => (
            <Button
              key={status}
              onClick={() => setFilter(status)}
              variant={filter === status ? 'default' : 'outline'}
              className={filter === status ? 'bg-violet-600' : 'border-white/20'}
            >
              {status === 'pending' && <AlertTriangle size={16} className="mr-2" />}
              {status === 'resolved' && <CheckCircle size={16} className="mr-2" />}
              {status === 'dismissed' && <XCircle size={16} className="mr-2" />}
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <Card className="glass-default border-white/10">
            <CardContent className="p-12 text-center">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
              <p className="text-xl text-white/60">No {filter} reports</p>
              <p className="text-sm text-white/40 mt-2">All clear!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <Card key={report.report_id} className="glass-default border-white/10">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Report Info */}
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReasonBadge(report.reason)}`}>
                          {formatReason(report.reason)}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-white/60 bg-white/5 px-2 py-1 rounded">
                          {getContentTypeIcon(report.content_type)}
                          {report.content_type}
                        </span>
                        <span className="text-xs text-white/40">
                          {new Date(report.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-white/80">{report.description}</p>

                      {/* Content Preview */}
                      {report.content_preview && (
                        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                          <p className="text-xs text-white/40 mb-2">Reported Content:</p>
                          {report.content_preview.image && (
                            <img 
                              src={report.content_preview.image} 
                              alt="Content" 
                              className="w-20 h-20 object-cover rounded-lg mb-2"
                            />
                          )}
                          {report.content_preview.name && (
                            <p className="font-medium">{report.content_preview.name}</p>
                          )}
                          {report.content_preview.text && (
                            <p className="text-sm text-white/60 italic">&ldquo;{report.content_preview.text}...&rdquo;</p>
                          )}
                          {report.content_preview.title && (
                            <p className="font-medium">{report.content_preview.title}</p>
                          )}
                        </div>
                      )}

                      {/* Reported User Info */}
                      {report.reported_user_name && (
                        <div className="flex items-center gap-2 text-sm text-white/60">
                          <User size={14} />
                          <span>Reported user: {report.reported_user_name}</span>
                          {report.reported_user_email && (
                            <span className="text-white/40">({report.reported_user_email})</span>
                          )}
                        </div>
                      )}

                      {/* Reporter */}
                      <p className="text-xs text-white/40">
                        Reported by: {report.reporter_name || 'Anonymous'}
                      </p>

                      {/* Action Taken (for resolved) */}
                      {report.action_taken && (
                        <div className={`flex items-center gap-2 text-sm ${
                          report.action_taken === 'user_banned' ? 'text-red-400' :
                          report.action_taken === 'content_removed' ? 'text-orange-400' :
                          report.action_taken === 'warning' ? 'text-yellow-400' :
                          'text-white/40'
                        }`}>
                          {report.action_taken === 'user_banned' && <Ban size={14} />}
                          {report.action_taken === 'content_removed' && <Trash2 size={14} />}
                          Action: {report.action_taken.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {filter === 'pending' && (
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => setSelectedReport(report)}
                          className="bg-violet-600 hover:bg-violet-500"
                        >
                          <Eye size={16} className="mr-2" />
                          Review
                        </Button>
                        <Button
                          onClick={() => handleResolve(report.report_id, 'dismiss')}
                          variant="outline"
                          className="border-white/20"
                        >
                          <XCircle size={16} className="mr-2" />
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {selectedReport && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="glass-default border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold">Review Report</h2>
                  <Button
                    variant="ghost"
                    onClick={() => { setSelectedReport(null); setAdminNotes(''); }}
                  >
                    <XCircle size={20} />
                  </Button>
                </div>

                {/* Report Details */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getReasonBadge(selectedReport.reason)}`}>
                      {formatReason(selectedReport.reason)}
                    </span>
                    <span className="text-white/60">{selectedReport.content_type}</span>
                  </div>
                  
                  <p className="text-white/80">{selectedReport.description}</p>

                  {selectedReport.content_preview && (
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <p className="text-xs text-white/40 mb-2">Reported Content:</p>
                      {selectedReport.content_preview.image && (
                        <img 
                          src={selectedReport.content_preview.image} 
                          alt="Content" 
                          className="w-full max-w-xs rounded-lg mb-2"
                        />
                      )}
                      {selectedReport.content_preview.text && (
                        <p className="text-white/70">&ldquo;{selectedReport.content_preview.text}&rdquo;</p>
                      )}
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div>
                    <label className="text-sm text-white/60 mb-2 block">Admin Notes (optional)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this decision..."
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30 focus:border-violet-500 outline-none"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleResolve(selectedReport.report_id, 'dismiss')}
                    variant="outline"
                    className="border-white/20"
                  >
                    <XCircle size={16} className="mr-2" />
                    Dismiss Report
                  </Button>
                  <Button
                    onClick={() => handleResolve(selectedReport.report_id, 'warning')}
                    className="bg-yellow-600 hover:bg-yellow-500"
                  >
                    <AlertTriangle size={16} className="mr-2" />
                    Issue Warning
                  </Button>
                  <Button
                    onClick={() => handleResolve(selectedReport.report_id, 'content_removed')}
                    className="bg-orange-600 hover:bg-orange-500"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Remove Content
                  </Button>
                  <Button
                    onClick={() => handleResolve(selectedReport.report_id, 'user_banned')}
                    className="bg-red-600 hover:bg-red-500"
                  >
                    <Ban size={16} className="mr-2" />
                    Ban User
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
